import _ from 'lodash'
import T from 'prop-types'
import {
  List,
  Set,
  fromJS
} from 'immutable'
import {
  getMockCounterData
} from './timeSeriesMocker'
import {
  TYPES
} from '../types'

const NULL_CHANCE = 0.25 // 25%

const LOREM = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed iaculis elit eget justo tempor, eu porta risus molestie. Nunc egestas consectetur tellus non interdum".split(' ')

const MOCK_WORDS = [
  'Widget',
  'Foo',
  'Baz',
  'Bar',
  'Fizz',
  'Buzz'
]

const arr = len => new Array(len).fill('')

const randomHex = bits => _.random(0, Math.pow(2, bits)).toString(16)

const generateLongText = len => {
  let out = ''
  while (out.length <= len) {
    out += _.sample(LOREM) + ' '
  }
  return out.substr(0, len)
}

const generateIPv4 = () => arr(4).map(() => _.random(0, 255)).join('.')

const generateIPv6 = () => {
  const willTruncate = Math.random() > 0.5
  let usedTruncate = false
  const segmentCt = willTruncate ? _.random(2, 6, false) : 8

  return  arr(segmentCt).map((s, i) => {
    const doTruncate = willTruncate && !usedTruncate && i > 0 && i < (segmentCt - 1)
    if (doTruncate) {
      usedTruncate = true
    }
    return doTruncate ? '' : randomHex(16)
  }).join(':')
}

const generateSubnetIPv4 = () => `${generateIPv4()}/${_.random(1,31)}`

const generateSubnetIPv6 = () => `${generateIPv6()}/${_.random(1,127)}`

const generateMacAddress = () => arr(6).map(() => {
  const r = `${randomHex(8)}`
  return r.length === 1 ? `0${r}` : r
}).join(':')

const generateMD5 = () => randomHex(128)

const generateSHA1 = () => randomHex(160)

const generateSHA256 = () => randomHex(256)

const generateSHA512 = () => randomHex(512)

const generateMockDomain = () => (_.compact([
  _.sample(['www', 'a', 'foo.biz', null]),
  _.sample(MOCK_WORDS).toLowerCase(),
  _.sample(['net', 'com', 'org', 'co.uk', 'ru'])
]).join('.'))

const generateMockURL = () => (_.compact([
  generateMockDomain(),
  _.sample(MOCK_WORDS).toLowerCase(),
  _.sample(MOCK_WORDS).toLowerCase(),
  _.sample(['?q=123', null])
])).join('/')

const generateDateString = () => {
  return `${_.random(1988, 2022)}-${_.padLeft(_.random(1, 12), 2, 0)}-${_.padLeft(_.random(1, 28), 2, 0)}T${_.padLeft(_.random(0,23), 2, 0)}:${_.padLeft(_.random(0,59), 2, 0)}:${_.padLeft(_.random(0,59), 2, 0)}`
}

const generateMockDate = () => new Date(generateDateString())

const mockNestedSchema = field => {
  const max = field.getIn(['editable', 'maxAllowed'], 10)
  const nestedSchema = field.get('schema')

  return List(arr(_.random(0, max)).map(() => mockEntry(nestedSchema)))
}

const mockList = field => {
  const max = field.getIn(['editable', 'maxAllowed'], 10)
  const listItem = field.get('listItem')
  return List(arr(_.random(0, max)).map(() => {
    return TYPES_TO_DATA_MOCKERS[listItem.get('type')](listItem)
  }))
}

const _getRandomOptions = (options, min, max) => {
  options = options.toJS ? options.toJS() : options

  let optionsArray = Array.isArray(options) ? options : Object.keys(options)

  // Start with a Set to eliminate any duplicates
  return Set(
    arr(_.random(min, max))
      .map(() => {
        const val = _.sample(optionsArray)
        optionsArray = _.pullAt(optionsArray, optionsArray.indexOf(val))
        return (typeof val === 'string' || !val) ? val : val.value
      })
  ).toList()
}

const mockEnum = field => {
  const min = field.get('required') ? 1 : 0
  const max = field.getIn(['editable', 'maxAllowed'], 10)
  const options = field.getIn(['editable', 'options'], field.get('options', []))
  const opts = _getRandomOptions(options, min, max)
  return max === 1 ? opts.first() : opts
}

const mockTags = field => {
  const min = field.get('required') ? 1 : 0
  const max = field.getIn(['editable', 'maxAllowed'], 10)
  const options = field.getIn(['editable', 'options'], field.get('options', []))
  return _getRandomOptions(options.concat(arr(_.random(5,10)).map(() => _.sample(LOREM))), min, max)
}

/**
 * Map of Schema Field Types to data generator/mocker functions.
 * Isolated from type definitions as these are not needed in production.
 */
export const TYPES_TO_DATA_MOCKERS = {
  SCHEMA: mockNestedSchema,
  LIST: mockList,
  ENUM: mockEnum,
  TAGS: mockTags,
  BOOL: () => _.sample([true, false]),
  NUMBER: field => _.random(field.getIn(['editable', 'min'], -9999), field.getIn(['editable', 'max'], 9999), false),
  INT: field => _.random(field.getIn(['editable', 'min'], -9999), field.getIn(['editable', 'max'], 9999), false),
  FLOAT: field => _.random(field.getIn(['editable', 'min'], -9999.5), field.getIn(['editable', 'max'], 9999.5), true),
  STRING: field => `${_.sample(MOCK_WORDS)} ${_.random(field.getIn(['editable', 'minLength'], 0), field.getIn(['editable', 'maxLength'], 100))}`,
  STRING_LONG: field => generateLongText(_.random(field.getIn(['editable', 'minLength'], 0), field.getIn(['editable', 'maxLength'], 1000))),
  BYTES: () => _.random(1, 9999999999),
  BYTES_PER_SEC: () => _.random(1, 99999999),
  DAY: () => generateMockDate().getDay(),
  TIME: () => generateMockDate().getTime(),
  DATETIME: () => +generateMockDate(),
  DURATION_MS: () => _.random(0, 99999), // millis?
  SUBNET: () => Math.random() > 0.88 ? generateIPv6() : generateIPv4(),
  SUBNET_V4: () => generateSubnetIPv4(),
  SUBNET_V6: () => generateSubnetIPv6(),
  IP: () => Math.random() > 0.88 ? generateIPv6() : generateIPv4(),
  IP_V4: () => generateIPv4(),
  IP_V6: () => generateIPv6(),
  MAC_ADDRESS: () => generateMacAddress(),
  PORT: () => _.random(0, Math.pow(2, 16)),
  DOMAIN: () => generateMockDomain(),
  URL: () => generateMockURL(),
  HASH: () => _.sample([generateMD5(), generateSHA1(), generateSHA256(), generateSHA512()]),
  HASH_MD5: () => generateMD5(),
  HASH_SHA1: () => generateSHA1(),
  HASH_SHA256: () => generateSHA256(),
  HASH_SHA512: () => generateSHA512()
}

export const mockEntry = (schema, idx) => {
  return schema.map((field, fieldName) => {
    if (fieldName === 'id') {
      return 'id_' + idx // Always mock "id" columns as sequential numbers?
    }
    const fieldType = field.get('type')
    if (TYPES_TO_DATA_MOCKERS.hasOwnProperty(fieldType)) {
      let mockVal = TYPES_TO_DATA_MOCKERS[fieldType](field)
      if (!field.get('required')) {
        // Roll for a null or undefined value, for non-required fields
        mockVal = Math.random() < NULL_CHANCE ? null : mockVal //_.sample([null, undefined, mockVal])
      }
      return mockVal
    }
    else {
      throw new Error(`Unknown field type: "${fieldType}"`)
    }

  })
}

const isNumericType = field => {
  const fieldPropType = field.get('propType')
  const fieldType = field.get('type')

  return (fieldPropType && fieldPropType === T.number)
    || fieldType === TYPES.INT
    || fieldType === TYPES.FLOAT
}

export const mockTimeSeries = (schema, startTime, endTime, intervalMs, mockSeedStr) => {
  const minTickValue = 1000
  const maxTickValue = 999999

  const DATETIME_TYPES = [TYPES.DATETIME, TYPES.DAY, TYPES.TIME]
  const timeFields = schema.filter(field => DATETIME_TYPES.includes(field.get('type')))

  const numericFields = schema
    .filter(isNumericType)
  const numericFieldKeys = numericFields.keySeq().toArray()

  const dataFields = numericFields
    .map((field, fieldName) => {
      const idx = numericFieldKeys.indexOf(fieldName)
      if (idx === 0) {
        return 80
      }
      else if (idx === 1) {
        return 50
      }
      else {
        return 10 + (idx * idx)
      }
    })
    .toJS()

  const ts = getMockCounterData(
    mockSeedStr,
    startTime,
    endTime,
    intervalMs,
    dataFields,
    maxTickValue,
    minTickValue,
    timeFields.keySeq().first()
  )

  return fromJS(ts)
}